/**
 * The predicates that decide whether stored credentials become a live session.
 * They sit outside the provider so they can be asserted directly: a browser
 * that arrived on the sign-in form already holding tokens used to be a dead end
 * that no reload could talk it out of, and that was a property of these rules.
 */

export type StoredSessionState = {
  /** Pages that render before a session exists (login, MFA steps, callbacks). */
  isOnPublicAuthPage: boolean
  /** The sign-in form itself. */
  isOnLoginPage: boolean
  hasAccessToken: boolean
  isCookieSessionOk: boolean
}

// Stored tokens answer "is there a session?" directly, so they make the cookie
// handoff redundant; an enforced logout means the browser is not asking.
export const shouldProbeCookieSession = ({
  hasAccessToken,
  isLogoutEnforced
}: {
  hasAccessToken: boolean
  isLogoutEnforced: boolean
}) => !isLogoutEnforced && !hasAccessToken

// Auth pages normally skip the profile call — nobody is signed in there — but on
// the login form a leftover token has to be settled: it either names a user (and
// the page hands them on) or it is dead (and the page drops it).
export const shouldResolveStoredSession = ({
  isOnPublicAuthPage,
  isOnLoginPage,
  hasAccessToken,
  isCookieSessionOk
}: StoredSessionState) =>
  (!isOnPublicAuthPage || isOnLoginPage) &&
  (isCookieSessionOk || hasAccessToken)

// A stored token the API refuses is worse than none: it also disables the cookie
// handoff behind it, so the page keeps a session that does not exist. Only an
// authorization refusal counts — a dropped request or a 500 says nothing about
// the token, and signing the member out over it would be its own bug.
export const shouldDropStoredSession = ({
  hasAccessToken,
  isProfileProbeRejected
}: {
  hasAccessToken: boolean
  isProfileProbeRejected: boolean
}) => hasAccessToken && isProfileProbeRejected

export type LoginPageExitState = {
  isOnLoginPage: boolean
  hasResolvedUser: boolean
  hasAccessToken: boolean
  isCookieSessionOk: boolean
  isLogoutEnforced: boolean
  /** A sign-in attempt owned by this page instance — it navigates on its own. */
  isLoginInProgress: boolean
}

export const shouldLeaveLoginPage = ({
  isOnLoginPage,
  hasResolvedUser,
  hasAccessToken,
  isCookieSessionOk,
  isLogoutEnforced,
  isLoginInProgress
}: LoginPageExitState) =>
  isOnLoginPage &&
  !isLogoutEnforced &&
  !isLoginInProgress &&
  (isCookieSessionOk || (hasResolvedUser && hasAccessToken))
